import { useState, useEffect } from "react";
import { useToast } from "../contexts/ToastContext";

interface ShareButtonProps {
  url: string;
  title: string;
  description?: string;
}

export function ShareButton({ url, title, description }: ShareButtonProps) {
  const { showToast } = useToast();
  const [showQRCode, setShowQRCode] = useState(false);
  const [shareUrl, setShareUrl] = useState(url);

  // 确保 URL 是完整的绝对 URL
  useEffect(() => {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      setShareUrl(url);
    } else {
      // 如果是相对路径，转换为绝对 URL
      setShareUrl(`${window.location.origin}${url.startsWith("/") ? url : `/${url}`}`);
    }
  }, [url]);

  // 复制链接到剪贴板
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast("链接已复制到剪贴板", "success");
    } catch (err) {
      // 降级方案
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        showToast("链接已复制到剪贴板", "success");
      } catch {
        showToast("复制失败，请手动复制", "error");
      }
      document.body.removeChild(textArea);
    }
  };

  // 微信分享 - 显示二维码
  const handleWeChatShare = () => {
    setShowQRCode(true);
  };

  // 生成二维码 URL（使用在线服务）
  const getQRCodeUrl = () => {
    const encodedUrl = encodeURIComponent(shareUrl);
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedUrl}`;
  };

  // 微博分享
  const handleWeiboShare = () => {
    const weiboUrl = `https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}${description ? `&content=${encodeURIComponent(description)}` : ""}`;
    window.open(weiboUrl, "_blank", "width=600,height=400");
  };

  // QQ 分享
  const handleQQShare = () => {
    const qqUrl = `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}${description ? `&summary=${encodeURIComponent(description)}` : ""}`;
    window.open(qqUrl, "_blank", "width=600,height=400");
  };

  // 原生 Web Share API
  const handleNativeShare = async () => {
    if ('share' in navigator) {
      try {
        await navigator.share({
          title,
          text: description || title,
          url: shareUrl,
        });
      } catch (err) {
        // 用户取消分享，不显示错误
        if ((err as Error).name !== "AbortError") {
          showToast("分享失败", "error");
        }
      }
    } else {
      // 不支持原生分享，降级到复制链接
      handleCopyLink();
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleCopyLink}
          className="px-3 py-2 rounded-lg border-2 border-white/40 bg-white/10 hover:bg-white/20 text-sm font-semibold transition hover:scale-105 flex items-center gap-2"
          title="复制链接"
        >
          <span>🔗</span>
          <span>复制链接</span>
        </button>

        <button
          onClick={handleWeChatShare}
          className="px-3 py-2 rounded-lg border-2 border-green-500/50 bg-green-500/20 hover:bg-green-500/30 text-sm font-semibold transition hover:scale-105 flex items-center gap-2"
          title="微信分享"
        >
          <span>💬</span>
          <span>微信</span>
        </button>

        <button
          onClick={handleWeiboShare}
          className="px-3 py-2 rounded-lg border-2 border-red-500/50 bg-red-500/20 hover:bg-red-500/30 text-sm font-semibold transition hover:scale-105 flex items-center gap-2"
          title="微博分享"
        >
          <span>📱</span>
          <span>微博</span>
        </button>

        <button
          onClick={handleQQShare}
          className="px-3 py-2 rounded-lg border-2 border-blue-500/50 bg-blue-500/20 hover:bg-blue-500/30 text-sm font-semibold transition hover:scale-105 flex items-center gap-2"
          title="QQ 分享"
        >
          <span>💙</span>
          <span>QQ</span>
        </button>

        {'share' in navigator && (
          <button
            onClick={handleNativeShare}
            className="px-3 py-2 rounded-lg border-2 border-purple-500/50 bg-purple-500/20 hover:bg-purple-500/30 text-sm font-semibold transition hover:scale-105 flex items-center gap-2"
            title="更多分享方式"
          >
            <span>📤</span>
            <span>分享</span>
          </button>
        )}
      </div>

      {/* 微信二维码弹窗 */}
      {showQRCode && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={() => setShowQRCode(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm mx-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowQRCode(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
            <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">微信分享</h3>
            <p className="text-sm text-gray-600 mb-4 text-center">扫描二维码在微信中打开</p>
            <div className="flex justify-center mb-4">
              <img src={getQRCodeUrl()} alt="分享二维码" className="w-48 h-48 border-2 border-gray-200 rounded-lg" />
            </div>
            <p className="text-xs text-gray-500 text-center">或复制链接发送给微信好友</p>
            <button
              onClick={handleCopyLink}
              className="mt-4 w-full px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition"
            >
              复制链接
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

